import React, { useState, useRef } from 'react';
import { api } from '../contexts/AuthContext';
import { promptApi } from '../services/api';
import { setSelectedBlueprintId } from '../hooks/useSelectedBlueprint';
import { SearchInput } from '../components/ui/SearchInput';
import { Button } from '../components/ui/button';
import { Tabs } from '../components/ui/tabs';
import Pagination from '../components/ui/pagination';
import { Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell } from '../components/ui/table';
import { Modal } from '../components/ui/modal';
import { PaginationInfo } from '../types';
import { MAX_BLUEPRINT_DESCRIPTION } from '../constants/validation';

interface ExploreResult {
  brand: string | null;
  country: string | null;
  countryCodes: string[] | null;
  strategy: string;
  metadata: {
    inputTokens?: number;
    outputTokens?: number;
    cost?: number;
  };
}

interface StreamedUrl {
  url: string;
  topics: string;
  created_at: string;
  index: number;
  confidence: number;
  isMatch: boolean;
  reasoning?: string;
}

interface ProgressUpdate {
  message: string;
  total?: number;
  processed?: number;
  found?: number;
}

const RESULTS_PAGE_SIZE = 10;

const EXAMPLE_PROMPTS = [
  'Nike in United States',
  'sports and fitness content',
  'sustainable travel audiences',
  'fintech news readers in SEA',
];

// Static, non-looping dot grid for the hero background — fades toward the
// edges via a radial mask and fades out entirely once docked. No ambient
// animation lives here; it never moves on its own.
const HERO_DOT_GRID_STYLE: React.CSSProperties = {
  backgroundImage:
    'radial-gradient(color-mix(in srgb, var(--accent) 20%, transparent) 1px, transparent 1px)',
  backgroundSize: '24px 24px',
  WebkitMaskImage:
    'radial-gradient(ellipse 55% 45% at 50% 38%, #000 0%, transparent 75%)',
  maskImage: 'radial-gradient(ellipse 55% 45% at 50% 38%, #000 0%, transparent 75%)',
};

const Explore: React.FC = () => {
  const [query, setQuery] = useState('');
  const [lastSubmittedQuery, setLastSubmittedQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ExploreResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [streamedUrls, setStreamedUrls] = useState<StreamedUrl[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [streamComplete, setStreamComplete] = useState(false);
  const [exploreComplete, setExploreComplete] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [progress, setProgress] = useState<ProgressUpdate | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [totalProcessed, setTotalProcessed] = useState(0);
  const [detailUrl, setDetailUrl] = useState<StreamedUrl | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isEditingBlueprint, setIsEditingBlueprint] = useState(false);
  const [editedBlueprint, setEditedBlueprint] = useState('');
  const [showDisagreeDialog, setShowDisagreeDialog] = useState(false);
  const [disagreeUrl, setDisagreeUrl] = useState<StreamedUrl | null>(null);
  const [disagreeFeedback, setDisagreeFeedback] = useState('');
  const [processingFeedback, setProcessingFeedback] = useState(false);
  const [urlFilter, setUrlFilter] = useState<'all' | 'match' | 'nomatch'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [showLowMatchDialog, setShowLowMatchDialog] = useState(false);
  const [expandingBlueprint, setExpandingBlueprint] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDescription, setSaveDescription] = useState('');
  const [savePrompt, setSavePrompt] = useState('');
  const [savingBlueprint, setSavingBlueprint] = useState(false);
  const [blueprintCollapsed, setBlueprintCollapsed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Docked = search bar lives at the top with the hero collapsed away.
  // Derived (not a separate flag toggled once) so returning to this page
  // with results already in state skips the hero entirely instead of
  // depending on remembering to also flip a "hasSearched" bit.
  const docked = loading || streaming || isPaused || exploreComplete || streamedUrls.length > 0 || Boolean(result);

  const handleExampleChipClick = (prompt: string) => {
    setQuery(prompt);
  };

  // Filter URLs based on selected filter
  const filteredUrls = streamedUrls.filter(url => {
    if (urlFilter === 'match') return url.isMatch;
    if (urlFilter === 'nomatch') return !url.isMatch;
    return true; // 'all'
  });

  // Paginate the filtered set, 10 per page
  const totalPages = Math.max(1, Math.ceil(filteredUrls.length / RESULTS_PAGE_SIZE));
  const activePage = Math.min(currentPage, totalPages);
  const pageStartIndex = (activePage - 1) * RESULTS_PAGE_SIZE;
  const pagedUrls = filteredUrls.slice(pageStartIndex, pageStartIndex + RESULTS_PAGE_SIZE);

  const handleFilterChange = (filter: 'all' | 'match' | 'nomatch') => {
    setUrlFilter(filter);
    setCurrentPage(1);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const newFiles = Array.from(files);
      setUploadedFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handlePauseResume = () => {
    if (isPaused) {
      // Resume - start a new streaming session with current result
      if (result) {
        setIsPaused(false);
        startUrlStreaming(result, false); // Don't clear URLs
      }
    } else {
      // Pause - abort the current stream
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        setIsPaused(true);
        setStreaming(false);
      }
    }
  };

  const handleContinueExploring = () => {
    if (result) {
      setExploreComplete(false);
      // Keep totalProcessed to maintain the count
      startUrlStreaming(result, false); // Don't clear URLs, append new ones
    }
  };

  const handleExpandBlueprint = async () => {
    if (!result) return;

    setExpandingBlueprint(true);

    try {
      // Call API to expand blueprint
      const response = await api.post('/api/explore/expand-blueprint', {
        currentBlueprint: result.strategy
      });

      const expandedBlueprint = response.data.expandedBlueprint;

      // Update result with expanded blueprint
      const updatedResult = {
        ...result,
        strategy: expandedBlueprint
      };

      setResult(updatedResult);
      setShowLowMatchDialog(false);

      // Clear URLs and restart analysis
      setStreamedUrls([]);
      setStreamComplete(false);
      setExploreComplete(false);
      setProgress(null);
      setTotalProcessed(0);
      setDetailUrl(null);
      setIsPaused(false);

      // Start streaming with updated strategy
      await startUrlStreaming(updatedResult, true);
    } catch (err: any) {
      console.error('Failed to expand blueprint:', err);
      setError('Failed to expand blueprint. Please try again.');
    } finally {
      setExpandingBlueprint(false);
    }
  };

  const handleExploreMore = () => {
    setShowLowMatchDialog(false);
    handleContinueExploring();
  };

  const handleStartEditBlueprint = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (result && !isEditingBlueprint) {
      // Pause any ongoing streaming
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        setIsPaused(true);
        setStreaming(false);
      }

      setEditedBlueprint(result.strategy);
      setIsEditingBlueprint(true);
    }
  };

  const handleCancelEditBlueprint = () => {
    setIsEditingBlueprint(false);
    setEditedBlueprint('');
  };

  const handleDisagree = (urlData: StreamedUrl) => {
    setDisagreeUrl(urlData);
    setShowDisagreeDialog(true);
    setDisagreeFeedback('');
  };

  const handleCancelDisagree = () => {
    setShowDisagreeDialog(false);
    setDisagreeUrl(null);
    setDisagreeFeedback('');
  };

  const handleSubmitDisagree = async () => {
    if (!result || !disagreeUrl) return;

    setProcessingFeedback(true);

    try {
      // Pause any ongoing streaming
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        setIsPaused(true);
        setStreaming(false);
      }

      // Determine the disagreement type
      const disagreementType = disagreeUrl.isMatch ? 'match_to_nomatch' : 'nomatch_to_match';

      // Call API to refine blueprint
      const response = await api.post('/api/explore/refine-blueprint', {
        currentBlueprint: result.strategy,
        urlSummary: disagreeUrl.topics,
        urlReasoning: disagreeUrl.reasoning || '',
        disagreementType,
        userFeedback: disagreeFeedback.trim() || null
      });

      const refinedBlueprint = response.data.refinedBlueprint;

      // Update result with refined blueprint
      const updatedResult = {
        ...result,
        strategy: refinedBlueprint
      };

      setResult(updatedResult);
      setShowDisagreeDialog(false);
      setDisagreeUrl(null);
      setDisagreeFeedback('');

      // Clear URLs and restart analysis
      setStreamedUrls([]);
      setStreamComplete(false);
      setExploreComplete(false);
      setProgress(null);
      setTotalProcessed(0);
      setDetailUrl(null);
      setIsPaused(false);

      // Start streaming with updated strategy
      await startUrlStreaming(updatedResult, true);
    } catch (err: any) {
      console.error('Failed to refine blueprint:', err);
      setError('Failed to refine blueprint. Please try again.');
    } finally {
      setProcessingFeedback(false);
    }
  };

  const handleSaveBlueprint = async () => {
    if (!result || !editedBlueprint.trim()) {
      return;
    }

    // Abort any ongoing streaming
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Update the result with new strategy
    const updatedResult = {
      ...result,
      strategy: editedBlueprint.trim()
    };

    setResult(updatedResult);
    setIsEditingBlueprint(false);
    setEditedBlueprint('');

    // Clear URLs and restart analysis
    setStreamedUrls([]);
    setStreamComplete(false);
    setExploreComplete(false);
    setProgress(null);
    setTotalProcessed(0);
    setDetailUrl(null);
    setIsPaused(false);

    // Start streaming with updated strategy
    await startUrlStreaming(updatedResult, true);
  };

  const handleOpenSaveDialog = () => {
    if (!result) return;

    // Pre-fill the dialog fields
    const brandCountryName = [result.brand, result.country].filter(Boolean).join(' + ');
    setSaveName(brandCountryName);
    setSaveDescription(lastSubmittedQuery);
    setSavePrompt(result.strategy);
    setShowSaveDialog(true);
  };

  const handleCloseSaveDialog = () => {
    setShowSaveDialog(false);
    setSaveName('');
    setSaveDescription('');
    setSavePrompt('');
  };

  const handleSubmitSaveBlueprint = async () => {
    // Validate all fields are filled
    if (!saveName.trim() || !saveDescription.trim() || !savePrompt.trim()) {
      setError('All fields are required to save the blueprint');
      return;
    }

    setSavingBlueprint(true);
    setError(null);

    try {
      const created = await promptApi.create({
        name: saveName.trim(),
        description: saveDescription.trim(),
        prompt: savePrompt.trim(),
        type: 'audience-targeting'
      });
      setSelectedBlueprintId(created.id);

      // Success - close dialog and reset fields
      handleCloseSaveDialog();

      // Show success message by temporarily setting a success state
      // (You could add a success message state if desired)
    } catch (err: any) {
      console.error('Failed to save blueprint:', err);
      setError(err.response?.data?.error || 'Failed to save blueprint. Please try again.');
    } finally {
      setSavingBlueprint(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!query.trim() && uploadedFiles.length === 0) {
      return;
    }

    // Abort any ongoing streaming
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setStreamedUrls([]);
    setStreamComplete(false);
    setExploreComplete(false);
    setProgress(null);
    setTotalProcessed(0);
    setDetailUrl(null);
    setIsPaused(false);
    setLastSubmittedQuery(query);

    try {
      let response;

      // If files are uploaded, use FormData
      if (uploadedFiles.length > 0) {
        const formData = new FormData();
        formData.append('query', query);
        uploadedFiles.forEach((file) => {
          formData.append('files', file);
        });

        response = await api.post<ExploreResult>(
          '/api/explore',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );
      } else {
        // Regular JSON request
        response = await api.post<ExploreResult>(
          '/api/explore',
          { query }
        );
      }

      setResult(response.data);

      // Then start streaming URLs
      await startUrlStreaming(response.data, true);
    } catch (err: any) {
      console.error('Explore error:', err);
      setError(
        err.response?.data?.error ||
        err.response?.data?.suggestion ||
        'Failed to generate brand blueprint. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const startUrlStreaming = async (strategyResult: ExploreResult, clearUrls: boolean = true) => {
    setStreaming(true);
    // Only clear URLs if explicitly requested
    if (clearUrls) {
      setStreamedUrls([]);
      setTotalProcessed(0);
    }
    setStreamComplete(false);
    setProgress(null);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const baseURL = api.defaults.baseURL || '';
      const endpoint = '/api/explore/stream-urls';
      const url = baseURL ? `${baseURL.replace(/\/$/, '')}${endpoint}` : endpoint;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          brand: strategyResult.brand,
          country: strategyResult.country,
          countryCodes: strategyResult.countryCodes,
          strategy: strategyResult.strategy
        }),
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error('Streaming failed');
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let currentEvent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.substring(7).trim();
          } else if (line.startsWith('data: ')) {
            const dataString = line.substring(6).trim();
            if (dataString) {
              try {
                const data = JSON.parse(dataString);

                if (currentEvent === 'url') {
                  setStreamedUrls(prev => [...prev, data]);
                  setTotalProcessed(prev => prev + 1);
                } else if (currentEvent === 'progress') {
                  setProgress(data);
                } else if (currentEvent === 'complete') {
                  setStreamComplete(true);
                  setExploreComplete(true);
                  setProgress(null);

                  // Check match ratio and show dialog if poor
                  setStreamedUrls(currentUrls => {
                    const matchCount = currentUrls.filter(u => u.isMatch).length;
                    const totalCount = currentUrls.length;
                    const matchRatio = totalCount > 0 ? matchCount / totalCount : 0;

                    console.log('Match ratio check:', { matchCount, totalCount, matchRatio });

                    // Show dialog if match ratio is worse than 1/50
                    if (matchRatio < 0.02 && totalCount >= 20) {
                      console.log('Showing low match dialog');
                      setShowLowMatchDialog(true);
                    }

                    return currentUrls; // Return unchanged
                  });
                } else if (currentEvent === 'error') {
                  setError(data.error);
                }

                currentEvent = '';
              } catch (e) {
                console.warn('Failed to parse SSE data:', dataString);
              }
            }
          }
        }
      }
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Streaming error:', err);
        // Only show error if we haven't received any URLs yet
        // If we have partial results, just mark as complete with what we have
        setStreamedUrls(currentUrls => {
          if (currentUrls.length === 0) {
            setError('Failed to stream URLs. Please try again.');
          } else {
            // We have partial results - mark as complete instead of error
            console.log(`Stream interrupted after ${currentUrls.length} URLs. Showing partial results.`);
            setStreamComplete(true);
            setExploreComplete(true);
          }
          return currentUrls;
        });
      }
    } finally {
      setStreaming(false);
      abortControllerRef.current = null;
    }
  };

  // Splits on **bold** markers and renders the bracketed segments as <strong>,
  // so the blueprint's markdown-lite emphasis shows as bold instead of raw
  // asterisks — editing still operates on the untouched raw string.
  const renderBoldText = (text: string) => {
    const parts = text.split(/(\*\*.+?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  };

  const renderFilterChips = () => {
    if (streamedUrls.length === 0) return null;
    const matchCount = streamedUrls.filter(u => u.isMatch).length;
    const noMatchCount = streamedUrls.filter(u => !u.isMatch).length;
    return (
      <div data-testid="explore-filter-chips" className="flex flex-shrink-0 items-center gap-2">
        <Tabs
          value={urlFilter}
          onChange={(value) => handleFilterChange(value as 'all' | 'match' | 'nomatch')}
          tabs={[
            { value: 'all', label: `All · ${streamedUrls.length.toLocaleString()}` },
            { value: 'match', label: `Matched · ${matchCount.toLocaleString()}` },
            { value: 'nomatch', label: `Unmatched · ${noMatchCount.toLocaleString()}` },
          ]}
        />
      </div>
    );
  };

  const matchBadgeClass = (isMatch: boolean) =>
    `inline-block rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap ${
      isMatch ? 'bg-safe-bg text-safe' : 'bg-ghost-bg text-ink-muted'
    }`;

  const confidenceBarClass = (confidence: number) =>
    confidence > 0.7 ? 'bg-safe' : confidence > 0.4 ? 'bg-warn' : 'bg-risk';

  const renderUrlRow = (urlData: StreamedUrl, absoluteIndex: number) => (
    <TableRow
      key={absoluteIndex}
      className="cursor-pointer"
      onClick={() => setDetailUrl(urlData)}
    >
      {/* URL is plain text — the whole row (including this cell) opens the
          modal. Only the open-icon cell next to it escapes to the site. */}
      <TableCell className="overflow-hidden">
        <span
          title={urlData.url}
          className="block"
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
        >
          {urlData.url}
        </span>
      </TableCell>
      <TableCell>
        <a
          href={urlData.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${urlData.url} in a new tab`}
          title="Open in new tab"
          className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-accent-active hover:bg-accent/15"
          onClick={(e) => e.stopPropagation()}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-[15px] w-[15px]">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            <polyline points="15 3 21 3 21 9" />
            <line x1="10" y1="14" x2="21" y2="3" />
          </svg>
        </a>
      </TableCell>
      <TableCell>
        <span className={matchBadgeClass(urlData.isMatch)}>
          {urlData.isMatch ? 'Match' : 'No Match'}
        </span>
      </TableCell>
      <TableCell className="text-ink-muted whitespace-nowrap">
        {Math.round(urlData.confidence * 100)}%
      </TableCell>
    </TableRow>
  );

  const renderUrlDetailModal = () => (
    <Modal
      open={Boolean(detailUrl)}
      onClose={() => setDetailUrl(null)}
      className="max-w-lg"
      title={
        detailUrl ? (
          <a
            href={detailUrl.url}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-accent-active hover:opacity-80 hover:underline"
          >
            {detailUrl.url}
          </a>
        ) : undefined
      }
    >
      {detailUrl && (
        <>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setDetailUrl(null)}
            className="absolute right-4 top-3.5 text-ink-muted transition-colors hover:text-ink"
          >
            ✕
          </button>

          {/* Badge + confidence bar + percentage */}
          <div className="mb-4 flex items-center gap-2.5">
            <span className={matchBadgeClass(detailUrl.isMatch)}>
              {detailUrl.isMatch ? 'Match' : 'No Match'}
            </span>
            <div className="h-2 flex-1 rounded-full bg-line">
              <div
                className={`h-2 rounded-full ${confidenceBarClass(detailUrl.confidence)}`}
                style={{ width: `${detailUrl.confidence * 100}%` }}
              />
            </div>
            <span className="text-xs font-medium text-ink">
              {Math.round(detailUrl.confidence * 100)}%
            </span>
          </div>

          <div className="mb-3.5">
            <div className="mb-1 text-xs font-semibold text-ink">Summary</div>
            <div className="text-xs leading-relaxed text-ink-muted break-words">
              {detailUrl.topics || 'No summary available'}
            </div>
          </div>

          {detailUrl.reasoning && (
            <div className="mb-3.5">
              <div className="mb-1 text-xs font-semibold text-ink">Reasoning</div>
              <div className="text-xs leading-relaxed text-ink-muted break-words">
                {detailUrl.reasoning}
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              const target = detailUrl;
              setDetailUrl(null);
              handleDisagree(target);
            }}
            className="w-full rounded border border-risk bg-risk-bg px-3 py-2 text-xs font-medium text-risk transition-colors hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-risk"
          >
            Disagree with this result
          </button>
        </>
      )}
    </Modal>
  );

  const renderPagination = () => {
    if (filteredUrls.length === 0) return null;
    const paginationInfo: PaginationInfo = {
      page: activePage,
      limit: RESULTS_PAGE_SIZE,
      totalCount: filteredUrls.length,
      totalPages,
      hasNextPage: activePage < totalPages,
      hasPreviousPage: activePage > 1,
      nextPage: activePage < totalPages ? activePage + 1 : null,
      previousPage: activePage > 1 ? activePage - 1 : null,
    };
    return (
      <div data-testid="explore-pagination" className="flex-shrink-0 border-t border-line bg-ghost-bg px-4 py-2.5">
        <Pagination pagination={paginationInfo} onPageChange={setCurrentPage} />
      </div>
    );
  };

  return (
    <div className="relative min-h-full">
      {/* Static, edge-fading dot grid — hero-only chrome, no ambient motion.
          Fades out (not removed) so the dock transition stays smooth. */}
      <div
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 transition-opacity duration-500 ${docked ? 'opacity-0' : 'opacity-100'}`}
        style={HERO_DOT_GRID_STYLE}
      />

      <div className="relative flex min-h-full flex-col">
        {/* Hero: centered headline/subtitle/chips that collapse away and a
            search pill that docks full-width on submit. */}
        <div
          data-testid="explore-hero"
          className={`flex flex-col items-center transition-[margin-top] duration-[550ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${
            // deepcontext-fbj1.3: docked top offset mirrors the Brand
            // Blueprints toolbar (SearchFilterBar.tsx's -mt-[21px]),
            // superseding the deepcontext-jlkv sidebar-centerline pull-up.
            docked ? '-mt-[21px]' : 'mt-16 sm:mt-28 lg:mt-40'
          }`}
        >
          <div
            aria-hidden={docked}
            className={`overflow-hidden text-center transition-all duration-[450ms] ${
              docked ? 'mb-0 max-h-0 opacity-0' : 'mb-1.5 max-h-32 opacity-100'
            }`}
          >
            <h1 className="text-2xl font-extrabold tracking-tight text-ink sm:text-[26px]">
              What should your brand{' '}
              <span className="bg-gradient-accent bg-clip-text text-transparent">live next to</span>?
            </h1>
            <p className="mt-1.5 text-xs text-ink-muted">
              Describe your targeting needs in plain language to discover matching content and inventory.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className={`relative w-full transition-[max-width] duration-[550ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${
              docked ? 'max-w-full' : 'max-w-[640px]'
            }`}
          >
            <SearchInput
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Describe your content targeting needs (e.g., 'Nike in United States' or 'sports and fitness content')"
              disabled={loading || streaming}
              attachmentSlot={
                <>
                  {/* File upload button inside input */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading || streaming}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-ink-muted hover:text-accent-active hover:bg-nav-active rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Upload files (PDF, DOC, PPT, JPEG)"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </>
              }
              actionButton={
                <>
                  {/* Show Explore/Explore Again/Continue Exploring button when query has changed OR when not streaming/paused OR when complete */}
                  {(query !== lastSubmittedQuery || (!streaming && !isPaused)) && (
                    <Button
                      type={(exploreComplete && query === lastSubmittedQuery) ? 'button' : 'submit'}
                      onClick={(exploreComplete && query === lastSubmittedQuery) ? handleContinueExploring : undefined}
                      disabled={loading || (!(exploreComplete && query === lastSubmittedQuery) && !query.trim() && uploadedFiles.length === 0)}
                      className="flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? 'Generating...' : ((exploreComplete && query === lastSubmittedQuery) ? 'Continue Exploring' : (query !== lastSubmittedQuery && lastSubmittedQuery ? 'Explore Again' : 'Explore'))}
                    </Button>
                  )}
                  {/* Show Pause/Continue button only when query hasn't changed AND (streaming or paused) AND not complete */}
                  {query === lastSubmittedQuery && (streaming || isPaused) && !exploreComplete && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handlePauseResume}
                      className="flex-shrink-0"
                    >
                      {/* Motion only during real activity: while streaming, the
                          spinner here replaces the old state ribbon. */}
                      {streaming && !isPaused && !exploreComplete && (
                        <span
                          data-testid="explore-pause-spinner"
                          aria-hidden="true"
                          className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/35 border-t-white"
                        />
                      )}
                      {isPaused ? 'Continue' : 'Pause'}
                    </Button>
                  )}
                </>
              }
            />

            {/* Uploaded files display */}
            {uploadedFiles.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {uploadedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-nav-active border border-line rounded-full text-sm"
                  >
                    <svg className="w-4 h-4 text-accent-active" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="text-ink max-w-xs truncate">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(index)}
                      disabled={loading || streaming}
                      className="text-ink-muted hover:text-risk transition-colors disabled:opacity-50"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </form>

          {/* Example-prompt chips — fill the search field, don't auto-submit */}
          <div
            aria-hidden={docked}
            className={`flex flex-wrap justify-center gap-2 overflow-hidden transition-all duration-[400ms] ${
              docked ? 'mt-0 max-h-0 opacity-0' : 'mt-4 max-h-16 opacity-100'
            }`}
          >
            {EXAMPLE_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                type="button"
                onClick={() => handleExampleChipClick(prompt)}
                disabled={loading || streaming}
                className="rounded-full border border-line bg-surface px-3.5 py-1.5 text-xs font-semibold text-ink-muted transition-colors hover:border-accent hover:text-accent-active disabled:cursor-not-allowed disabled:opacity-50"
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-6 flex-shrink-0 rounded-lg border border-risk bg-risk-bg p-4">
            <p className="text-sm text-risk">{error}</p>
          </div>
        )}

        {/* Docked results: filter chips, full-width blueprint band, results panel */}
        {docked && result && (
          <div className="mt-6 flex flex-col gap-3">
            {/* Full-width Brand Blueprint band */}
            <div data-testid="explore-blueprint-band" className="flex-shrink-0 rounded-2xl border border-line bg-surface shadow-sm">
              <div className="flex items-center gap-3.5 border-b border-line px-5 py-3.5">
                <span className="text-[11px] font-bold uppercase tracking-wide text-ink-muted">
                  Brand Blueprint
                </span>

                {(result.brand || result.country) && (
                  <span className="text-sm font-extrabold text-ink">
                    {result.brand}
                    {result.brand && result.country && (
                      <i className="not-italic px-1.5 font-normal text-ink-muted">·</i>
                    )}
                    {result.country}
                  </span>
                )}

                <div className="flex-1" />

                {!isEditingBlueprint && (
                  <Button onClick={handleOpenSaveDialog} size="sm">
                    Save Brand Blueprint
                  </Button>
                )}

                <button
                  type="button"
                  onClick={() => setBlueprintCollapsed((collapsed) => !collapsed)}
                  aria-label={blueprintCollapsed ? 'Expand Brand Blueprint' : 'Collapse Brand Blueprint'}
                  title={blueprintCollapsed ? 'Expand' : 'Collapse'}
                  className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border border-line text-ink-muted transition-colors hover:text-ink"
                >
                  {blueprintCollapsed ? '▾' : '▴'}
                </button>
              </div>

              {!blueprintCollapsed && (
                <div className="p-5">
                  {isEditingBlueprint ? (
                    <div className="space-y-2">
                      <textarea
                        value={editedBlueprint}
                        onChange={(e) => setEditedBlueprint(e.target.value)}
                        className="w-full text-xs text-ink leading-relaxed border border-line rounded-xl p-2 focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent resize-none"
                        rows={6}
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={handleSaveBlueprint}
                          disabled={!editedBlueprint.trim()}
                          size="sm"
                        >
                          Save
                        </Button>
                        <Button
                          onClick={handleCancelEditBlueprint}
                          variant="ghost"
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="w-full text-xs text-ink leading-relaxed p-3 -mx-3 rounded-xl border-2 border-transparent cursor-pointer hover:border-accent/30 hover:bg-nav-active transition-all"
                      onClick={handleStartEditBlueprint}
                      title="Click to edit"
                    >
                      {renderBoldText(result.strategy)}
                    </div>
                  )}
                </div>
              )}
            </div>

            {renderFilterChips()}

            {/* Results panel, full width below the filter chips — content-height
                card like JobPerformance's table card (JobPerformance.tsx:891),
                not a fixed viewport box: page size is capped at
                RESULTS_PAGE_SIZE, so there's never enough rows to need one. */}
            <div data-testid="explore-results-panel" className="relative min-w-0 overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
              {streamedUrls.length === 0 && !streaming && !streamComplete && !isPaused ? (
                <div className="p-12 text-center text-ink-muted">
                  URLs will appear here as they are found...
                </div>
              ) : (
                <>
                  <div data-testid="explore-url-list" className="min-w-0 overflow-x-auto bg-surface">
                    <Table className="table-fixed">
                      <TableHead>
                        <tr>
                          <TableHeaderCell className="w-[52%]">URL</TableHeaderCell>
                          <TableHeaderCell className="w-[40px]">{null}</TableHeaderCell>
                          <TableHeaderCell>Match</TableHeaderCell>
                          <TableHeaderCell>Confidence</TableHeaderCell>
                        </tr>
                      </TableHead>
                      {pagedUrls.length > 0 && (
                        <TableBody>
                          {pagedUrls.map((urlData, localIndex) => renderUrlRow(urlData, pageStartIndex + localIndex))}
                        </TableBody>
                      )}
                    </Table>
                    {(streaming || isPaused) && pagedUrls.length === 0 && (
                      <div className="flex flex-col items-center gap-2.5 py-14 text-ink-muted">
                        <span
                          data-testid="explore-finding-urls-spinner"
                          aria-hidden="true"
                          className="h-6 w-6 animate-spin rounded-full border-2 border-accent/25 border-t-accent"
                        />
                        <span className="text-sm">Finding URLs…</span>
                      </div>
                    )}
                  </div>
                  {pagedUrls.length > 0 && renderPagination()}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* URL detail modal — replaces the old inline row expansion */}
      {renderUrlDetailModal()}

      {/* Disagree Dialog */}
      {showDisagreeDialog && disagreeUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-[18px] shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-ink mb-4">
              Provide Feedback
            </h3>

            <div className="mb-4">
              <p className="text-sm text-ink mb-2">
                You disagree that this URL is a <span className="font-semibold">{disagreeUrl.isMatch ? 'match' : 'no match'}</span>.
              </p>
              <div className="bg-ghost-bg p-3 rounded-xl border border-line">
                <p className="text-xs text-ink-muted font-medium mb-1">URL Summary:</p>
                <p className="text-xs text-ink">{disagreeUrl.topics}</p>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-ink-muted mb-2">
                Additional feedback (optional):
              </label>
              <textarea
                value={disagreeFeedback}
                onChange={(e) => setDisagreeFeedback(e.target.value)}
                placeholder="Explain why you disagree with this classification..."
                className="w-full px-3 py-2 text-sm border border-line bg-surface text-ink rounded-xl focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent resize-none"
                rows={4}
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleSubmitDisagree}
                disabled={processingFeedback}
                className="flex-1"
              >
                {processingFeedback ? 'Refining Blueprint...' : 'Refine Blueprint'}
              </Button>
              <Button
                onClick={handleCancelDisagree}
                disabled={processingFeedback}
                variant="ghost"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Low Match Ratio Dialog */}
      {showLowMatchDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-[18px] shadow-xl max-w-lg w-full p-6 relative">
            {/* Close button */}
            <button
              onClick={() => setShowLowMatchDialog(false)}
              className="absolute top-4 right-4 text-ink-muted hover:text-ink transition-colors"
              aria-label="Close dialog"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <h3 className="text-lg font-semibold text-ink mb-4">
              Limited Matching Inventory
            </h3>

            <div className="mb-6">
              <p className="text-sm text-ink">
                We couldn't find a lot of inventory matching your criteria. You can either expand your blueprint to find more relevant content, or continue exploring with the current blueprint.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleExpandBlueprint}
                disabled={expandingBlueprint}
                className="flex-1"
              >
                {expandingBlueprint ? 'Expanding Blueprint...' : 'Expand Blueprint'}
              </Button>
              <Button
                onClick={handleExploreMore}
                variant="ghost"
                className="flex-1"
              >
                Explore More Content
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Save Brand Blueprint Dialog */}
      {showSaveDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface rounded-[18px] shadow-xl max-w-2xl w-full p-6">
            <h3 className="text-lg font-semibold text-ink mb-4">
              Save Brand Blueprint
            </h3>

            <div className="space-y-4 mb-6">
              {/* Name Field */}
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-2">
                  Name <span className="text-risk">*</span>
                </label>
                <input
                  type="text"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  placeholder="e.g., Nike + United States"
                  className="w-full px-3 py-2 text-sm border border-line bg-surface text-ink rounded-xl focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
                />
                <p className="mt-1 text-xs text-ink-muted">
                  {result?.brand || result?.country
                    ? 'Pre-filled with brand and country (you can edit it)'
                    : 'Enter a name for this blueprint'}
                </p>
              </div>

              {/* Description Field */}
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-2">
                  Description <span className="text-risk">*</span>
                </label>
                <textarea
                  value={saveDescription}
                  onChange={(e) => setSaveDescription(e.target.value)}
                  placeholder="Describe the targeting needs for this blueprint"
                  className="w-full px-3 py-2 text-sm border border-line bg-surface text-ink rounded-xl focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent resize-none"
                  rows={2}
                  maxLength={MAX_BLUEPRINT_DESCRIPTION}
                />
                <div className="mt-1 flex items-center justify-between">
                  <p className="text-xs text-ink-muted">
                    Pre-filled with your original query
                  </p>
                  <p className={`text-xs ${saveDescription.length >= MAX_BLUEPRINT_DESCRIPTION ? 'text-risk' : 'text-ink-muted'}`}>
                    {saveDescription.length}/{MAX_BLUEPRINT_DESCRIPTION}
                  </p>
                </div>
              </div>

              {/* Prompt Field */}
              <div>
                <label className="block text-sm font-medium text-ink-muted mb-2">
                  Blueprint <span className="text-risk">*</span>
                </label>
                <textarea
                  value={savePrompt}
                  onChange={(e) => setSavePrompt(e.target.value)}
                  placeholder="The brand blueprint strategy"
                  className="w-full px-3 py-2 text-sm border border-line bg-surface text-ink rounded-xl focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent resize-none"
                  rows={6}
                />
                <p className="mt-1 text-xs text-ink-muted">
                  The generated brand blueprint
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleSubmitSaveBlueprint}
                disabled={savingBlueprint || !saveName.trim() || !saveDescription.trim() || !savePrompt.trim()}
                className="flex-1"
              >
                {savingBlueprint ? 'Saving...' : 'Save'}
              </Button>
              <Button
                onClick={handleCloseSaveDialog}
                disabled={savingBlueprint}
                variant="ghost"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Explore;
